import React, { ReactElement } from 'react';
import { Box, Typography, IconButton } from "@mui/material";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';

interface ServiceEvent {
    dateServiced: string;
    id?: number | string;
    serviceType?: string;
    groupLabel?: string;
}

interface ServiceFlagTooltipProps {
    serviceEvents: ServiceEvent[];
    currentEventIndex: number;
    onNext: (e: React.MouseEvent) => void;
    onPrevious: (e: React.MouseEvent) => void;
    iconFinder: (serviceType?: string) => ReactElement;
    formatServiceType: (type?: string) => string;
    getServiceColor: (serviceType?: string) => string;
}

const ServiceFlagTooltip: React.FC<ServiceFlagTooltipProps> = ({
    serviceEvents, currentEventIndex, onNext, onPrevious, iconFinder,
    formatServiceType, getServiceColor,
}) => {
    const currentEvent = serviceEvents[currentEventIndex];
    const hasMultipleEvents = serviceEvents.length > 1;
    const navigate = useNavigate();

    if (!currentEvent) return null;

    const handleTooltipClick = () => {
        if (!currentEvent?.serviceType || currentEvent.id === undefined || currentEvent.id === null) {
            return;
        }
        // Map service type to work history sheet type (e.g., 'sheet' -> 'sheets')
        let sheetType = currentEvent.serviceType.toLowerCase();
        if (sheetType === 'sheet') {
            sheetType = 'sheets';
        }
        const sheetId = String(currentEvent.id);
        const searchParams = new URLSearchParams({ sheetType, sheetId });
        navigate(
            { pathname: '/work-history', search: `?${searchParams.toString()}` },
            { state: { sheetType, sheetId, id: currentEvent.id } }
        );
    };

    return (
        <Box
            sx={{
                p: 1,
                maxWidth: 320,
                bgcolor: 'white',
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: '#f5f5f5',
                }
            }}
            onClick={handleTooltipClick}
        >
            {/* Header with navigation for multiple events */}
            {hasMultipleEvents && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    pb: 0.5,
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPrevious(e);
                        }}
                        sx={{
                            minWidth: 'auto',
                            width: 24,
                            height: 24,
                            color: 'black',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <ArrowBackIosIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'black' }}>
                        {currentEventIndex + 1} of {serviceEvents.length} events
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNext(e);
                        }}
                        sx={{
                            minWidth: 'auto',
                            width: 24,
                            height: 24,
                            color: 'black',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                </Box>
            )}

            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: getServiceColor(currentEvent.serviceType),
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `2px solid ${getServiceColor(currentEvent.serviceType)}`,
                    pb: 0.5,
                }}
            >
                <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center', fontSize: '1.2em' }}>
                    {iconFinder(currentEvent.serviceType)}
                </Box>
                {formatServiceType(currentEvent.serviceType)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'black' }}>
                📅 <strong>Date:</strong> {new Date(currentEvent.dateServiced).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })}
            </Typography>
            {currentEvent.id && (
                <Typography variant="body2" sx={{ mb: 0.5, color: 'black' }}>
                    🆔 <strong>Service ID:</strong> {currentEvent.id}
                </Typography>
            )}
            {currentEvent.groupLabel && (
                <Typography variant="body2" sx={{ mb: 0.5, color: 'black' }}>
                    📋 <strong>Group:</strong> {currentEvent.groupLabel}
                </Typography>
            )}
            <Typography
                variant="caption"
                sx={{
                    mt: 1,
                    display: 'block',
                    textAlign: 'center',
                    color: '#1976d2',
                    fontStyle: 'italic',
                    borderTop: '1px solid #e0e0e0',
                    pt: 0.5
                }}
            >
                Click to view in Work History
            </Typography>
        </Box>
    );
};

export default ServiceFlagTooltip;